import {makeAutoObservable} from "mobx";
import {DayWeekObservable} from "./dayWeekObservable";
import {DayWeekState} from "../../shared/enums/eventDayWeekState";
import {forEach, isEqual} from "lodash";
import {EventHelper} from "../../shared/eventHelper";
import i18n from "i18next";

export class WeekObservableList {
  monthWeekList: DayWeekObservable[];
  // Used for tracking changes to weeks
  originalWeeks: number[] = [];

  constructor(
    monthWeeks?: number[],
    eventStartDate?: Date,
    timezoneCode?: string
  ) {
    makeAutoObservable(this);

    this.originalWeeks = monthWeeks ?? [];
    // Initialize weekList with default values
    this.monthWeekList = [
      new DayWeekObservable(
        1,
        DayWeekState.default,
        "event:labels:weeks:first:label",
        "event:labels:weeks:first:name"
      ),
      new DayWeekObservable(
        2,
        DayWeekState.default,
        "event:labels:weeks:second:label",
        "event:labels:weeks:second:name"
      ),
      new DayWeekObservable(
        3,
        DayWeekState.default,
        "event:labels:weeks:third:label",
        "event:labels:weeks:third:name"
      ),
      new DayWeekObservable(
        4,
        DayWeekState.default,
        "event:labels:weeks:fourth:label",
        "event:labels:weeks:fourth:name"
      ),
      new DayWeekObservable(
        -1,
        DayWeekState.default,
        "event:labels:weeks:last:label",
        "event:labels:weeks:last:name"
      )
    ];

    const fourthWeek = 4,
      lastWeek = -1;
    if (monthWeeks && eventStartDate && timezoneCode) {
      forEach(monthWeeks, (eventWeek) => {
        const selectedWeek = this.monthWeekList.find(
          (value) => value.index === eventWeek
        );

        if (selectedWeek) {
          let selectedWeekNumber = EventHelper.getWeekOfMonth(
            eventStartDate!,
            timezoneCode!
          );
          const isEventStartWeek = selectedWeekNumber === eventWeek;

          // forth and last week should not be auto selected.
          if (
            selectedWeekNumber === fourthWeek ||
            selectedWeekNumber === lastWeek
          ) {
            selectedWeek.state = DayWeekState.selected;
          } else {
            selectedWeek.state = isEventStartWeek
              ? DayWeekState.autoSelected
              : DayWeekState.selected;
          }
        }
      });

      this.disableEnableRemainingWeeks();
    }
  }

  clearSelections() {
    this.monthWeekList.forEach((value) => (value.state = DayWeekState.default));
  }

  disableEnableRemainingWeeks() {
    const selectedWeekNumbers = this.getSelectedWeeks();
    if (selectedWeekNumbers.length >= 3) {
      this.setMonthWeekState(DayWeekState.default, DayWeekState.disabled);
    } else {
      this.setMonthWeekState(DayWeekState.disabled, DayWeekState.default);
    }
  }

  setMonthWeekState(currentState: DayWeekState, newState: DayWeekState) {
    forEach(this.monthWeekList, (value) => {
      if (value.state === currentState) {
        value.state = newState;
      }
    });
  }

  getDisplayLabel() {
    const selectedWeeks = this.getSelectedWeeks().map(
      (value) => `$t(${value.fullLabel})`
    );

    if (selectedWeeks.length === 0) return "";
    if (selectedWeeks.length === 1) return i18n.t(selectedWeeks[0]);

    const lastSelected = selectedWeeks.pop();
    return i18n.t("event:messages.recurrenceSummaryDayWeekMultiple", {
      firstValues: selectedWeeks.join(", "),
      lastValue: lastSelected
    });
  }

  getSelectedWeeks() {
    return this.monthWeekList.filter(
      (value) =>
        value.state === DayWeekState.selected ||
        value.state === DayWeekState.autoSelected
    );
  }

  getSelectedWeekNumbers(): number[] {
    const weeks = this.getSelectedWeeks().map((value) => value.index);
    return weeks;
  }

  getWeek(index: number): DayWeekObservable | undefined {
    return this.monthWeekList.find((value) => value.index === index);
  }

  hasChange() {
    return !isEqual(this.originalWeeks, this.getSelectedWeekNumbers());
  }
}
